import { useEffect, useMemo, useRef, useState } from 'react'
import {
  BarChart3,
  BriefcaseBusiness,
  Crop,
  Download,
  FileText,
  MoreHorizontal,
  Palette,
  Upload,
  UsersRound,
} from 'lucide-react'
import { toast } from 'sonner'
import useStore from '@/stores/useStore'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { ToggleSetting } from '@/components/ui/toggle-setting'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import LogoCropper from '@/components/LogoCropper'
import SectionToggleList from '@/components/report/SectionToggleList'
import { cn, downloadFile } from '@/lib/utils'
import { downscaleToDataUrl, renderSquareCrop, DEFAULT_CROP } from '@/lib/imageUtils'
import {
  DEFAULT_REPORT_TITLE,
  REPORT_SECTIONS,
  isSectionEnabled,
  parseReportProfile,
  serializeReportProfile,
} from '@/lib/reportConfig'
import { HTML_REPORT_SECTIONS } from '@/lib/htmlExport/interactive'
import { resolveProposalReportSettings } from '@/lib/proposalReportModel'

const COVER_FORMATS = new Set(['detailed-pdf', 'detailed-html', 'full-detail-html', 'prospect-pdf', 'prospect-html'])
const PROSPECT_FORMATS = new Set(['prospect-pdf', 'prospect-html'])
const CONTENT_FORMATS = new Set(['detailed-pdf', 'detailed-html'])

const OUTLINE = [
  { key: 'cover', label: 'Cover', icon: FileText },
  { key: 'executiveSummary', label: 'Executive summary', icon: BriefcaseBusiness, content: true },
  { key: 'costAnalysis', label: 'Cost analysis', icon: BarChart3, content: true },
  { key: 'employeeImpact', label: 'Employee impact', icon: UsersRound, content: true },
  { key: 'branding', label: 'Branding', icon: Palette },
]

function collectDescendants(registry, rootKey) {
  const children = registry.filter((section) => section.parent === rootKey)
  return children.flatMap((child) => [child, ...collectDescendants(registry, child.key)])
}

export default function ReportStudioSettings({ format }) {
  const reportConfig = useStore((state) => state.reportConfig)
  const setReportConfig = useStore((state) => state.setReportConfig)
  const toggleReportSection = useStore((state) => state.toggleReportSection)
  const baselinePlan = useStore((state) => state.planA)
  const logoInputRef = useRef(null)
  const profileInputRef = useRef(null)
  const [cropperOpen, setCropperOpen] = useState(false)
  const [activeSection, setActiveSection] = useState('cover')
  const [profileMenuOpen, setProfileMenuOpen] = useState(false)

  const hasCover = COVER_FORMATS.has(format)
  const isProspect = PROSPECT_FORMATS.has(format)
  const isHtml = format === 'detailed-html' || format === 'full-detail-html'
  const supportsContent = CONTENT_FORMATS.has(format)
  const sectionRegistry = isHtml ? HTML_REPORT_SECTIONS : REPORT_SECTIONS
  const outlineItems = useMemo(() => OUTLINE.filter((item) => !item.content || supportsContent), [supportsContent])
  const activeItem = outlineItems.find((item) => item.key === activeSection) || outlineItems[0]

  const proposalSettings = resolveProposalReportSettings(reportConfig, baselinePlan?.newCompProposal)
  const fundingCorridor = baselinePlan?.newCompProposal?.reportInputs?.fundingCorridor
  const hasSourcedFunding = fundingCorridor?.recommended !== null
    && fundingCorridor?.recommended !== undefined
    && Boolean(String(fundingCorridor?.source || '').trim())

  useEffect(() => {
    if (!outlineItems.some((item) => item.key === activeSection)) setActiveSection('cover')
  }, [activeSection, format, outlineItems])

  const updateProposalSetting = (key, value) => setReportConfig({
    proposal: {
      ...(reportConfig.proposal || {}),
      [key]: value === '' ? null : Number(value),
    },
  })

  const onLogoChange = async (event) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    if (file.size > 8 * 1024 * 1024) {
      toast.error('Choose a logo smaller than 8 MB.')
      return
    }
    if (!file.type.startsWith('image/')) {
      toast.error('Choose a PNG, JPG, or SVG logo.')
      return
    }
    try {
      const source = await downscaleToDataUrl(file)
      setReportConfig({ logoSource: source, logoCrop: DEFAULT_CROP })
      setCropperOpen(true)
    } catch {
      toast.error('The logo could not be read. Choose another image.')
    }
  }

  const handleCropSave = async (crop) => {
    try {
      const logoDataUrl = await renderSquareCrop(reportConfig.logoSource, crop)
      setReportConfig({ logoCrop: crop, logoDataUrl })
    } catch {
      toast.error('The logo could not be processed. Try another image.')
    }
    setCropperOpen(false)
  }

  const onImportProfile = (event) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    if (file.size > 12 * 1024 * 1024) {
      toast.error('Choose a report settings file smaller than 12 MB.')
      return
    }
    const reader = new FileReader()
    reader.onload = () => {
      try {
        setReportConfig(parseReportProfile(String(reader.result)))
        toast.success('Report settings loaded')
      } catch {
        toast.error('Choose a valid Retira report settings file.')
      }
    }
    reader.readAsText(file)
  }

  if (!hasCover) return null

  const renderCover = () => {
    const fields = [
      { key: 'title', label: 'Report title', placeholder: 'Annual plan review…', helper: `Blank uses “${DEFAULT_REPORT_TITLE}”`, maxLength: 240 },
      { key: 'clientName', label: 'Client or plan sponsor', placeholder: 'Example: Northstar Manufacturing…', required: true, maxLength: 160 },
      { key: 'firmName', label: 'Firm name', placeholder: 'Example: Northstar Retirement…', maxLength: 160 },
      { key: 'preparedBy', label: 'Prepared by', placeholder: 'Example: Jordan Lee…', maxLength: 160 },
    ]
    return (
      <section className="space-y-4" aria-labelledby="report-settings-cover">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 id="report-settings-cover" className="text-sm font-semibold">Cover</h3>
            <p className="mt-1 text-[11px] leading-4 text-muted-foreground">Set the client-facing title and attribution.</p>
          </div>
          <button
            type="button"
            onClick={() => setReportConfig({ title: '', clientName: '', firmName: '', preparedBy: '' })}
            className="shrink-0 rounded text-[11px] font-medium text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
          >
            Restore cover defaults
          </button>
        </div>
        {fields.map((field) => {
          const value = reportConfig[field.key] || ''
          const invalid = field.required && !value.trim()
          const inputId = `report-${field.key}`
          return (
            <div key={field.key} className="space-y-1.5">
              <label htmlFor={inputId} className="block text-[11px] font-medium text-foreground/80">
                {field.label}{field.required ? ' *' : ''}
              </label>
              <Input
                id={inputId}
                name={inputId}
                autoComplete="off"
                maxLength={field.maxLength}
                value={value}
                placeholder={field.placeholder}
                aria-required={field.required || undefined}
                aria-invalid={invalid || undefined}
                aria-describedby={field.helper ? `${inputId}-help` : undefined}
                onChange={(event) => setReportConfig({ [field.key]: event.target.value })}
                className="h-9 text-xs"
              />
              {field.helper ? <p id={`${inputId}-help`} className="text-[10px] leading-4 text-muted-foreground">{field.helper}</p> : null}
              {invalid ? <p className="text-[10px] text-destructive">Enter a client or plan sponsor name to preview the report.</p> : null}
            </div>
          )
        })}
      </section>
    )
  }

  const renderBranding = () => (
    <section className="space-y-4" aria-labelledby="report-settings-branding">
      <div>
        <h3 id="report-settings-branding" className="text-sm font-semibold">Branding</h3>
        <p className="mt-1 text-[11px] leading-4 text-muted-foreground">Choose which name and logo lead the report.</p>
      </div>
      <ToggleSetting
        title="Use firm branding"
        description="Use your firm name and logo. A small Retira.app credit remains in the footer."
        checked={Boolean(reportConfig.debrand)}
        onCheckedChange={(value) => setReportConfig({ debrand: value })}
      />
      {reportConfig.debrand ? (
        <div className="space-y-3 border border-border bg-muted/20 p-3">
          <div className="flex items-center gap-2">
            <img
              src={reportConfig.logoDataUrl || '/logo.png'}
              alt={reportConfig.logoDataUrl ? 'Firm logo preview' : 'Retira logo preview'}
              width="40"
              height="40"
              className="h-10 w-10 rounded object-contain"
            />
            <Button type="button" variant="outline" size="sm" className="h-8 gap-1 px-2 text-[11px]" onClick={() => logoInputRef.current?.click()}>
              <Upload className="h-3 w-3" aria-hidden="true" />{reportConfig.logoDataUrl ? 'Replace logo' : 'Upload logo'}
            </Button>
            {reportConfig.logoSource ? (
              <Button type="button" variant="ghost" size="sm" className="h-8 gap-1 px-2 text-[11px]" onClick={() => setCropperOpen(true)}>
                <Crop className="h-3 w-3" aria-hidden="true" />Adjust
              </Button>
            ) : null}
          </div>
          {reportConfig.logoDataUrl ? (
            <button type="button" onClick={() => setReportConfig({ logoSource: null, logoCrop: null, logoDataUrl: null })} className="rounded text-[11px] font-medium text-muted-foreground hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50">
              Remove logo
            </button>
          ) : null}
          <input ref={logoInputRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" onChange={onLogoChange} />
          {!reportConfig.firmName?.trim() && !reportConfig.logoDataUrl ? (
            <p className="text-[10px] leading-4 text-amber-800">Add a firm name on Cover or upload a logo to activate firm branding.</p>
          ) : null}
        </div>
      ) : null}
    </section>
  )

  const renderAssumptions = () => {
    if (isHtml || activeSection !== 'costAnalysis') return null
    const assumptions = [
      ['marginalTaxRatePercent', 'Marginal tax rate', proposalSettings.marginalTaxRatePercent, '0.5', 0, 60],
      ['projectionYears', 'Projection years', proposalSettings.projectionYears, '1', 1, 30],
      ['annualContributionGrowthPercent', 'Annual growth', proposalSettings.annualContributionGrowthPercent, '0.5', 0, 15],
      ['interestCreditAssumptionPercent', 'Interest override', '', '0.25', 0, 15],
    ]
    return (
      <details className="border-t border-border pt-4" open={isProspect || undefined}>
        <summary className="cursor-pointer text-xs font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50">Tax &amp; projection assumptions</summary>
        <div className="mt-3 grid grid-cols-2 gap-3">
          {assumptions.map(([key, label, fallback, step, min, max]) => {
            const id = `report-proposal-${key}`
            return (
              <div key={key} className="space-y-1">
                <label htmlFor={id} className="block text-[10px] font-medium text-foreground/75">{label}{key !== 'projectionYears' ? ' (%)' : ''}</label>
                <Input
                  id={id}
                  name={id}
                  autoComplete="off"
                  inputMode="decimal"
                  type="number"
                  min={min}
                  max={max}
                  step={step}
                  value={reportConfig.proposal?.[key] ?? fallback}
                  placeholder={key === 'interestCreditAssumptionPercent' ? 'Use plan rate…' : undefined}
                  onChange={(event) => updateProposalSetting(key, event.target.value)}
                  className="h-8 text-xs"
                />
              </div>
            )
          })}
        </div>
        <p className={cn('mt-3 text-[10px] leading-4', hasSourcedFunding ? 'text-muted-foreground' : 'text-amber-800')}>
          {hasSourcedFunding
            ? `Tax savings use the actuary's recommended funding amount from ${fundingCorridor.source}.`
            : 'Cash-balance tax savings remain blank until an actuary supplies a funding amount.'}
        </p>
      </details>
    )
  }

  const renderContent = () => {
    const root = sectionRegistry.find((section) => section.key === activeSection)
    const sections = collectDescendants(sectionRegistry, activeSection)
    return (
      <section className="space-y-4" aria-labelledby={`report-settings-${activeSection}`}>
        <div>
          <h3 id={`report-settings-${activeSection}`} className="text-sm font-semibold">{activeItem.label}</h3>
          <p className="mt-1 text-[11px] leading-4 text-muted-foreground">Choose the pages and detail included in this chapter.</p>
        </div>
        {root ? (
          <ToggleSetting
            title={`Include ${activeItem.label.toLowerCase()}`}
            description="Turn this off to remove the entire chapter."
            checked={isSectionEnabled(reportConfig, root.key, sectionRegistry)}
            onCheckedChange={(value) => toggleReportSection(root.key, value)}
          />
        ) : null}
        {isSectionEnabled(reportConfig, activeSection, sectionRegistry) ? (
          <SectionToggleList
            sections={sections}
            isEnabled={(key) => isSectionEnabled(reportConfig, key, sectionRegistry)}
            onToggle={toggleReportSection}
          />
        ) : (
          <p className="border-l-2 border-border pl-3 text-[11px] leading-4 text-muted-foreground">This chapter is excluded from the report.</p>
        )}
        {renderAssumptions()}
      </section>
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-card">
      <div className="flex shrink-0 items-center justify-between border-b border-border px-3 py-2">
        <p className="text-xs font-semibold">Report outline</p>
        <Popover open={profileMenuOpen} onOpenChange={setProfileMenuOpen}>
          <PopoverTrigger asChild>
            <button type="button" aria-label="Open report settings menu" className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50">
              <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" sideOffset={6} className="w-48 gap-1 p-1.5">
            <button
              type="button"
              className="flex h-9 w-full items-center gap-2 rounded px-2 text-left text-xs hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
              onClick={() => {
                setProfileMenuOpen(false)
                downloadFile(serializeReportProfile(reportConfig), 'retira-report-settings.json', 'application/json')
                toast.success('Report settings saved')
              }}
            >
              <Download className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />Save settings
            </button>
            <button type="button" className="flex h-9 w-full items-center gap-2 rounded px-2 text-left text-xs hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50" onClick={() => { setProfileMenuOpen(false); profileInputRef.current?.click() }}>
              <Upload className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />Load settings
            </button>
          </PopoverContent>
        </Popover>
        <input ref={profileInputRef} type="file" accept="application/json,.json" className="hidden" onChange={onImportProfile} />
      </div>

      <nav aria-label="Report outline" className="shrink-0 border-b border-border p-2">
        <ul className="grid grid-cols-1 gap-1">
          {outlineItems.map((item) => {
            const Icon = item.icon
            const active = activeItem.key === item.key
            const enabled = !item.content || isSectionEnabled(reportConfig, item.key, sectionRegistry)
            return (
              <li key={item.key}>
                <button
                  type="button"
                  onClick={() => setActiveSection(item.key)}
                  aria-current={active ? 'page' : undefined}
                  className={cn(
                    'flex min-h-9 w-full min-w-0 items-center gap-2 rounded px-2.5 text-left text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50',
                    active ? 'bg-foreground text-background' : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                  )}
                >
                  <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  <span className="truncate">{item.label}</span>
                  {item.content ? <span className={cn('ml-auto h-1.5 w-1.5 rounded-full', enabled ? (active ? 'bg-background' : 'bg-emerald-600') : 'bg-muted-foreground/40')} aria-hidden="true" /> : null}
                </button>
              </li>
            )
          })}
        </ul>
      </nav>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4">
        {activeSection === 'cover' ? renderCover() : activeSection === 'branding' ? renderBranding() : renderContent()}
      </div>

      {cropperOpen ? (
        <LogoCropper
          open={cropperOpen}
          source={reportConfig.logoSource}
          initialCrop={reportConfig.logoCrop}
          onSave={handleCropSave}
          onCancel={() => setCropperOpen(false)}
        />
      ) : null}
    </div>
  )
}
